import {
  Component,
  inject,
  output,
  computed,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import {
  selectEnvironmentType,
  selectEnvironmentLabel,
  selectEnvironmentInfo,
  selectIsPreview,
} from '@core/state/environment';
import { DatabaseDisplayNamePipe } from '@shared/pipes/database-display-name/database-display-name.pipe';
import { SignalRStore } from '@core/services/signal-r/signal-r.service';
import { AppConfigService } from '@core/services/config/app-config.service';
import { toSignal } from '@angular/core/rxjs-interop';

/**
 * Small environment indicator that appears in the header/footer.
 * Clicking it opens the version info dialog.
 */
@Component({
  selector: 'app-environment-indicator',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule],
  template: `
    <button
      class="environment-indicator"
      [class]="'environment-indicator--' + environmentType()"
      [matTooltip]="tooltipText()"
      (click)="clicked.emit()"
      aria-label="View environment and version info"
    >
      @if (isPreview()) {
        <mat-icon class="environment-indicator__icon">science</mat-icon>
        <span class="environment-indicator__label"
          >PR #{{ info()?.prNumber }}</span
        >
      } @else if (!info()?.isProduction) {
        <mat-icon class="environment-indicator__icon">code</mat-icon>
        <span class="environment-indicator__label">{{ displayLabel() }}</span>
      } @else {
        <span class="environment-indicator__label">{{
          productionChipLabel()
        }}</span>
      }
      <span
        class="live-indicator"
        [class.live-indicator--connected]="liveConnected()"
        [class.live-indicator--disabled]="!liveEnabled()"
        [matTooltip]="liveTooltip()"
        matTooltipPosition="above"
        >●</span
      >
    </button>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      :host {
        margin: 0 8px;
      }

      .environment-indicator {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 4px 8px;
        border: none;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 500;
        cursor: pointer;
        transition:
          opacity 0.2s,
          transform 0.1s;
      }

      .environment-indicator:hover {
        opacity: 0.9;
        transform: scale(1.02);
      }

      .environment-indicator:active {
        transform: scale(0.98);
      }

      .environment-indicator--production {
        background: #e8f5e9;
        color: #2e7d32;
      }

      .environment-indicator--staging {
        background: #fff3e0;
        color: #e65100;
      }

      .environment-indicator--preview {
        background: #f3e5f5;
        color: #7b1fa2;
      }

      .environment-indicator--development {
        background: #e3f2fd;
        color: #1565c0;
      }

      .environment-indicator--local {
        background: #eceff1;
        color: #455a64;
      }

      .environment-indicator__icon {
        font-size: 14px;
        height: 14px;
        width: 14px;
      }

      .environment-indicator__label {
        white-space: nowrap;
      }

      .live-indicator {
        font-size: 8px;
        line-height: 1;
        color: #ef5350;
        margin-left: 2px;
        transition: color 0.3s ease;
      }

      .live-indicator--connected {
        color: #4caf50;
      }

      .live-indicator--disabled {
        color: #bdbdbd;
      }
    `,
  ],
})
export class EnvironmentIndicatorComponent {
  private readonly store = inject(Store);
  private readonly dbDisplayNamePipe = new DatabaseDisplayNamePipe();
  private readonly signalRStore = inject(SignalRStore);
  private readonly appConfig = inject(AppConfigService);

  // Store selectors as signals
  protected readonly environmentType = this.store.selectSignal(
    selectEnvironmentType
  );
  protected readonly environmentLabel = this.store.selectSignal(
    selectEnvironmentLabel
  );
  protected readonly info = this.store.selectSignal(selectEnvironmentInfo);
  protected readonly isPreview = this.store.selectSignal(selectIsPreview);

  /** Web app's own build-time semantic version (e.g., "7.37.2"). */
  protected readonly webVersion = computed(
    () => this.appConfig.config.gitVersion
  );

  /**
   * Label shown in the production-environment chip. Renders the web app
   * version when it is a real semver; otherwise falls back to the environment
   * label so a misconfigured deploy never shows "v", "vCHANGEME", or "vunknown".
   */
  protected readonly productionChipLabel = computed(() => {
    const v = this.webVersion();
    if (v && v !== 'CHANGEME' && v !== 'unknown') {
      return `v${v}`;
    }
    return this.displayLabel();
  });

  // Live updates status
  protected readonly liveEnabled = computed(
    () => this.appConfig.config.signalRActive
  );
  protected readonly liveConnected = toSignal(
    this.signalRStore.select((state) => state.connected),
    { initialValue: false }
  );
  protected readonly liveTooltip = computed(() => {
    if (!this.liveEnabled()) return 'Live updates disabled';
    return this.liveConnected()
      ? 'Live updates active'
      : 'Live updates disconnected';
  });

  /** Emitted when the indicator is clicked */
  readonly clicked = output<void>();

  /** Display-friendly label for the environment */
  protected readonly displayLabel = computed(() => {
    const label = this.environmentLabel();
    const lower = label?.toLowerCase();
    if (lower === 'development' || lower === 'local') {
      return 'Local Development';
    }
    return label;
  });

  protected readonly tooltipText = computed(() => {
    const info = this.info();
    if (!info) return 'Loading...';

    const envName = info.environment?.toLowerCase();
    const isLocal = envName === 'development' || envName === 'local';

    // Local development gets a simpler tooltip
    if (isLocal) {
      const dbInfo =
        info.databaseName && info.databaseName !== 'unknown'
          ? ` · ${this.dbDisplayNamePipe.transform(info.databaseName, info.databaseType)}`
          : '';
      return `Local Development (not versioned)${dbInfo}`;
    }

    const parts: string[] = [];

    // Environment name
    if (info.environment) {
      parts.push(info.environment);
    }

    // Database (skip if unknown or placeholder)
    if (info.databaseName && info.databaseName !== 'unknown') {
      parts.push(
        this.dbDisplayNamePipe.transform(info.databaseName, info.databaseType)
      );
    }

    // Web app version (skip if placeholder)
    const webVersion = this.webVersion();
    if (webVersion && webVersion !== 'CHANGEME' && webVersion !== 'unknown') {
      parts.push(`v${webVersion}`);
    }

    // PR number for preview environments
    if (info.prNumber) {
      parts.push(`PR #${info.prNumber}`);
    }

    return parts.join(' · ');
  });
}
